<template>
  <v-app id="app" class="app-shell" :class="{ 'app-shell--authenticated': isAuthenticatedRoute }">
    <v-dialog v-model="openExit" width="600" persistent transition="dialog-top-transition">
      <v-card>
        <v-card-title></v-card-title>
        <v-card-text>
          <h2 class="black--text">Deseja realmente sair?</h2>
        </v-card-text>
        <v-card-actions class="d-flex justify-space-between">
          <v-btn elevation="0" text @click="openExit = !openExit"> Não </v-btn>
          <v-btn elevation="0" dark color="#e5183d" @click="handleLogout"> Sim </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-navigation-drawer
      v-if="showAuthenticatedShell"
      v-model="drawer"
      app
      class="app-drawer"
    >
      <v-list-item class="drawer-brand">
        <v-list-item-content class="text-center">
          <v-list-item-avatar class="mb-2">
            <v-img width="60" height="50" contain :src="logo" alt="LOGO BRP"></v-img>
          </v-list-item-avatar>

          <v-list-item-subtitle class="drawer-brand__subtitle">
            {{ profileAreaLabel }}
          </v-list-item-subtitle>
          <div v-if="isAssociatePremiumActive" class="drawer-brand__premium mt-2">
            <premium-member-badge size="x-small" />
          </div>
        </v-list-item-content>
      </v-list-item>

      <v-divider></v-divider>

      <v-list dense nav>
        <v-list-item
          v-for="item in navigationItems"
          :key="item.title"
          :to="item.href ? undefined : item.to"
          :href="item.href"
          link
          :class="{ 'drawer-item--highlight': item.highlight }"
          :target="item.href ? '_blank' : undefined"
          :rel="item.href ? 'noopener noreferrer' : undefined"
          @click="handleChangeTitle(item.title)"
        >
          <v-list-item-content>
            <v-list-item-title class="d-flex align-center">
              {{ item.title }}
              <v-chip
                v-if="item.chip"
                x-small
                color="#FFD54F"
                text-color="#1a1a2e"
                class="ml-2 font-weight-bold"
              >
                {{ item.chip }}
              </v-chip>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-app-bar
      v-if="showAuthenticatedShell"
      app
      absolute
      elevation="0"
      height="66.8px"
      class="app-topbar"
    >
      <v-app-bar-nav-icon @click="drawer = !drawer"></v-app-bar-nav-icon>
      <v-toolbar-title class="d-flex align-center">
        <span>{{ title }}</span>
        <premium-member-badge
          v-if="isAssociatePremiumActive"
          class="ml-3 hidden-sm-and-down"
          size="x-small"
        />
      </v-toolbar-title>

      <v-spacer></v-spacer>
      <v-btn icon @click="openExit = !openExit">
        <v-icon>mdi-exit-to-app</v-icon>
      </v-btn>
    </v-app-bar>

    <v-main class="app-content">
      <div
        v-if="showGlobalPremiumUpsell"
        class="global-premium-upsell ma-4 mb-0"
      >
        <premium-upsell-banner
          variant="compact"
          title="Desbloqueie comissões da sua rede"
          subtitle="Torne-se Premium e receba até R$ 100 por adesão direta na rede."
          cta-label="Ver Minha Rede"
          :show-secondary="false"
          @cta="goToPremiumUpsell"
        />
        <v-btn icon x-small class="global-premium-upsell__close" @click="dismissPremiumUpsell">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>
      <v-alert
        v-if="isAuthenticatedRoute && isFranchiseAssociateInactive"
        type="warning"
        prominent
        border="left"
        colored-border
        class="associate-inactive-banner ma-4 mb-0"
      >
        <strong>Conta inativa.</strong>
        Seu perfil de associado está inativo. Você pode navegar e visualizar as listagens, mas não cadastrar novos
        registros. Em caso de dúvidas, entre em contato com o suporte.
      </v-alert>
      <premium-renewal-alert
        v-if="showGlobalPremiumRenewalBanner"
        class="ma-4 mb-0"
        :show="true"
        :expired="isAssociatePremiumExpired"
        :days-until-expiry="associatePremiumDaysUntilExpiry"
        :expires-at="associatePremiumExpiresAt"
        @renew="goToPremiumRenewal"
      />
      <v-alert
        v-if="isAuthenticatedRoute && showPremiumPagbankBanner"
        type="warning"
        prominent
        border="left"
        colored-border
        class="associate-inactive-banner ma-4 mb-0"
      >
        Você possui ganhos disponíveis na sua rede, porém precisa conectar sua conta PagBank
        para receber os valores.
        <v-btn class="mt-2" small color="#e5183d" dark :to="{ name: 'bankAccount' }">
          Conectar PagBank
        </v-btn>
      </v-alert>
      <v-alert
        v-else-if="isAuthenticatedRoute && isFranchiseAssociatePending"
        type="warning"
        prominent
        border="left"
        colored-border
        class="associate-inactive-banner ma-4 mb-0"
      >
        <strong>Cadastro em análise.</strong>
        Seu perfil de associado ainda está pendente de aprovação. Enquanto isso, você pode navegar pelo painel,
        mas precisa aguardar a aprovação para cadastrar parceiros.
      </v-alert>
      <v-snackbar
        v-model="snackbar.active"
        :color="snackbar.color"
        :right="true"
        :top="true"
        :timeout="snackbar.timer"
      >
        {{ snackbar.message }}
        <template #action="{ attrs }">
          <v-icon color="white" v-bind="attrs" @click="snackbar.active = false">mdi-close</v-icon>
        </template>
      </v-snackbar>
      <page-loading
        v-if="shouldShowSessionLoading"
        variant="fullscreen"
        message="Carregando seus dados"
        subtitle="Estamos preparando seu painel"
        :show-preview="false"
      />
      <router-view v-else :class="contentShellClass" />
    </v-main>
  </v-app>
</template>

<script>
import PageLoading from "@/components/loading/PageLoading.vue";
import PremiumMemberBadge from "@/components/premium/PremiumMemberBadge.vue";
import PremiumUpsellBanner from "@/components/premium/PremiumUpsellBanner.vue";
import PremiumRenewalAlert from "@/components/premium/PremiumRenewalAlert.vue";
import { labelForAssociateCareerRole } from "@/config/associateCareer";
import { mapGetters, mapState } from "vuex";

const PREMIUM_UPSELL_DISMISS_KEY = "premium-upsell-dismissed";
const PREMIUM_UPSELL_PAGE_ROUTES = new Set([
  "home",
  "partners",
  "associates",
  "bankAccount",
]);

export default {
  name: "App",
  components: {
    PageLoading,
    PremiumMemberBadge,
    PremiumUpsellBanner,
    PremiumRenewalAlert,
  },

  data: () => ({
    drawer: null,
    openExit: false,
    title: "Home",
    logo: require("./assets/logo.png"),
    premiumUpsellDismissed: sessionStorage.getItem(PREMIUM_UPSELL_DISMISS_KEY) === "1",
  }),

  created() {
    this.initializeSession();
  },

  computed: {
    ...mapState(["snackbar", "sessionLoading"]),
    ...mapGetters([
      "canAccessAssociates",
      "isAssociatesRegistrar",
      "isAssociateProfile",
      "isCollaboratorManager",
      "isFranchiseAssociateInactive",
      "isFranchiseAssociatePending",
      "isFirstLogin",
      "shouldShowPremiumUpsell",
      "isAssociatePremiumRenewalDue",
      "isAssociatePremiumExpired",
      "isAssociatePremiumActive",
    ]),
    isMinimalShellRoute() {
      return ["login", "forgotPassword", "firstAccess", "referralSignup"].includes(
        String(this.$route.name || ""),
      );
    },
    showAuthenticatedShell() {
      return !this.isMinimalShellRoute;
    },
    isAuthenticatedRoute() {
      return !this.isMinimalShellRoute;
    },
    contentShellClass() {
      if (!this.isAuthenticatedRoute) return "pa-0";
      const compact = this.$route.meta && this.$route.meta.compactShell;
      return compact ? "content-shell pa-2" : "content-shell pa-6";
    },
    shouldShowSessionLoading() {
      if (!this.isAuthenticatedRoute) {
        return false;
      }
      const hasTokens = !!localStorage.getItem("tokens");
      return hasTokens && (this.sessionLoading || !this.$store.state.session);
    },
    profileAreaLabel() {
      if (this.isAssociateProfile) {
        const role = this.$store.state.session?.associateCareerRole;
        return labelForAssociateCareerRole(role);
      }
      return "Franqueado";
    },
    tutorialsUrl() {
      return this.isAssociateProfile
        ? "https://site.brpoints.com.br/tutoriais/?c=associado"
        : "https://site.brpoints.com.br/tutoriais/?c=franquia";
    },
    associatePremiumExpiresAt() {
      return this.$store.state.session?.associatePremiumExpiresAt ?? null;
    },
    associatePremiumDaysUntilExpiry() {
      const value = this.$store.state.session?.associatePremiumDaysUntilExpiry;
      return typeof value === "number" ? value : null;
    },
    showGlobalPremiumRenewalBanner() {
      if (!this.isAuthenticatedRoute) return false;
      if (this.$route.name === "associates") return false;
      return this.isAssociatePremiumRenewalDue || this.isAssociatePremiumExpired;
    },
    showPremiumPagbankBanner() {
      const session = this.$store.state.session;
      return (
        !!session?.associateId &&
        !!session?.isAssociatePremiumActive &&
        !session?.pagbankConnect
      );
    },
    showGlobalPremiumUpsell() {
      if (!this.isAuthenticatedRoute) return false;
      if (!this.shouldShowPremiumUpsell) return false;
      if (this.premiumUpsellDismissed) return false;
      if (PREMIUM_UPSELL_PAGE_ROUTES.has(String(this.$route.name))) return false;
      return true;
    },
    navigationItems() {
      const items = [
        { title: "Dashboard", to: "/" },
        { title: "Parceiros", to: "/partners" },
        { title: "Transações", to: "/transactions" },
        { title: "Dados Bancários", to: "/bank-account" },
        { title: "Tutoriais", href: this.tutorialsUrl },
      ];
      if (!this.canAccessAssociates) {
        return items;
      }
      const associatesLabel =
        this.isAssociateProfile || this.isCollaboratorManager
          ? "Minha Rede"
          : "Associados";
      items.splice(3, 0, {
        title: associatesLabel,
        to: "/associates",
        highlight: this.shouldShowPremiumUpsell,
        chip: this.shouldShowPremiumUpsell ? "Ganhe +" : undefined,
      });
      return items;
    },
  },
  watch: {
    $route: {
      handler() {
        this.enforceFirstAccess();
        this.syncTitleByRoute();
      },
      immediate: true,
    },
    profileAreaLabel() {
      this.syncTitleByRoute();
    },
    isFirstLogin() {
      this.enforceFirstAccess();
    },
  },

  methods: {
    handleChangeTitle(title) {
      this.title = title;
      this.updateBrowserTabTitle(title);
    },
    resolveTitleByRoute() {
      const routeName = this.$route.name;
      if (routeName === "home") return this.profileAreaLabel;
      if (routeName === "partners") return "Parceiros";
      if (routeName === "partnerStore") return "Configurar loja";
      if (routeName === "transactions") return "Transações";
      if (routeName === "bankAccount") return "Dados Bancários";
      if (routeName === "associates") {
        return this.isAssociateProfile || this.isCollaboratorManager
          ? "Minha Rede"
          : "Associados";
      }
      return this.title || this.profileAreaLabel;
    },
    updateBrowserTabTitle(title) {
      document.title = `${title} - BRPoints`;
    },
    syncTitleByRoute() {
      if (!this.isAuthenticatedRoute) return;
      const resolvedTitle = this.resolveTitleByRoute();
      this.title = resolvedTitle;
      this.updateBrowserTabTitle(resolvedTitle);
    },
    handleLogout() {
      this.openExit = false;
      this.$store.dispatch("clearSession");
      localStorage.removeItem("tokens");
      this.$router.push("/login");
    },
    async initializeSession() {
      const tokens = localStorage.getItem("tokens");
      if (!tokens || this.$store.state.session) return;
      try {
        await this.$store.dispatch("loadSession");
        this.enforceFirstAccess();
      } catch {
        this.$store.dispatch("clearSession");
        localStorage.removeItem("tokens");
        if (this.$route.name !== "login") {
          this.$router.replace("/login");
        }
      }
    },
    goToPremiumUpsell() {
      this.$router.push({ name: "associates", query: { adesao: "premium" } });
    },
    goToPremiumRenewal() {
      this.$router.push({ name: "associates", query: { adesao: "premium" } });
    },
    dismissPremiumUpsell() {
      this.premiumUpsellDismissed = true;
      sessionStorage.setItem(PREMIUM_UPSELL_DISMISS_KEY, "1");
    },
    enforceFirstAccess() {
      const hasTokens = !!localStorage.getItem("tokens");
      if (!hasTokens || !this.$store.state.session) {
        return;
      }
      const currentRoute = String(this.$route.name || "");
      if (this.isFirstLogin && currentRoute !== "firstAccess") {
        this.$router.replace("/first-access");
        return;
      }
      if (!this.isFirstLogin && currentRoute === "firstAccess") {
        this.$router.replace("/");
      }
    },
  },
};
</script>

<style scoped>
.drawer-brand {
  background: #E72A4C;
  border-radius: 0;
  margin-bottom: 4px;
}

.drawer-brand__subtitle {
  color: #ffffff !important;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.drawer-brand__premium {
  display: flex;
  justify-content: center;
}

.drawer-brand .v-list-item__title,
.drawer-brand .v-list-item__subtitle,
.drawer-brand .v-list-item__content {
  color: #ffffff !important;
}

.app-drawer ::v-deep .drawer-brand .v-list-item__subtitle {
  color: #ffffff !important;
}

.content-shell {
  max-width: 1440px;
  margin: 0 auto;
}

.global-premium-upsell {
  position: relative;
}

.global-premium-upsell__close {
  position: absolute !important;
  top: 6px;
  right: 6px;
  background: rgba(0, 0, 0, 0.2) !important;
  color: #fff !important;
}

.drawer-item--highlight {
  background: rgba(229, 24, 61, 0.06);
  border-left: 3px solid #e5183d;
}
</style>
